import { useQuery } from '@tanstack/react-query';
import { useDavepiConfig } from '../config/ConfigProvider.js';
import { probeAgent, type AgentInfo } from './client.js';

/**
 * Resolved agent state for the admin shell.
 *
 * - `configured` — the app declares an agent (`config.agent.url`) and it is
 *   not disabled.
 * - `available` — the configured agent answered `GET /health` as a davepi
 *   agent. Only when this is `true` should the UI swap the dashboard for a
 *   chat panel; otherwise fall back to the plain dashboard.
 * - `info` — the agent's identity payload (name / team roster) once probed.
 */
export interface AgentState {
  configured: boolean;
  available: boolean;
  url: string | null;
  /** Effective chat heading: `config.agent.title` ?? `info.name`. */
  title: string | null;
  placeholder: string;
  info: AgentInfo | null;
  isPending: boolean;
}

/**
 * Detect whether this admin fronts a `@davepi/agent` and, if so, whether it
 * is reachable right now. Reads `config.agent` and probes the agent's
 * `/health`. The probe result is cached for the session and retried on
 * reconnect, so a chat panel appears the moment the agent process comes up
 * without a manual refresh — but a down agent doesn't wedge the dashboard.
 *
 * @example
 * const agent = useAgent();
 * if (agent.available) return <AgentChat />;
 * return <ResourceCards />;
 */
export function useAgent(): AgentState {
  const { config } = useDavepiConfig();
  const agentCfg = config.agent;
  const url = agentCfg?.url ?? null;
  const configured = Boolean(url) && agentCfg?.enabled !== false;

  const query = useQuery({
    queryKey: ['davepi', 'agent-health', url ?? '__none__'],
    enabled: configured,
    // Identity is near-static, but liveness isn't: keep it fresh enough that
    // starting/stopping the agent flips the dashboard within a minute.
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: ({ signal }) => probeAgent(url!, signal),
  });

  const info = query.data ?? null;
  return {
    configured,
    // Only swap to chat when the agent is reachable AND not self-reporting
    // unhealthy (`ok: false`) — a degraded agent falls back to the dashboard.
    available: configured && info != null && info.ok !== false,
    url,
    title: agentCfg?.title ?? info?.name ?? null,
    placeholder: agentCfg?.placeholder ?? 'Ask the assistant…',
    info,
    isPending: configured && query.isPending,
  };
}
